import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
  useAdminAtualizarRedeMutation,
  useAdminCriarRedeMutation,
  useAdminListarLojasCandidatasRedeQuery,
  useAdminListarLojasDaRedeQuery,
  useAdminListarRedesQuery,
  useAdminMeQuery,
  useAdminSalvarLojasDaRedeMutation,
} from '../../../api';
import { HeaderTela } from '../../../components/admin/header-tela';
import { ApresentacaoRede } from './apresentacao';
import { RedeDoFranqueado } from './franqueado';

function mensagemDoErro(err: unknown, padrao: string): string {
  return (err as { data?: { mensagem?: string } })?.data?.mensagem ?? padrao;
}

const DESCRICAO_TELA =
  'Agrupe as lojas da franquia para criar cupons que valem em todas elas — e uma única vez por cliente na rede inteira.';

/**
 * Rede de lojas.
 *
 * A tela é visível para TODA empresa, de propósito: quem ainda não é franquia vê
 * o que o recurso faz e pode pedir a liberação dali mesmo. O conteúdo se adapta
 * a três situações — franqueador (monta a rede), franqueado (só consulta) e
 * quem não tem o módulo (apresentação + CTA).
 *
 * Isso vale só para ESTA tela: na tela de cupom, os campos de rede continuam
 * escondidos de quem não é franquia. Ver `docs/specs/cupom-rede.md` §7.
 */
export function AdminRedePage(): JSX.Element {
  const { data: me, isLoading: carregandoMe } = useAdminMeQuery();

  if (carregandoMe) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (me?.redeGerenciavel) return <RedeGerenciar />;
  if (me?.redeVisivel) return <RedeDoFranqueado />;
  return <ApresentacaoRede />;
}

// ---------------------------------------------------------------------------
// Franqueador: monta a rede. (Os outros dois estados vivem em apresentacao.tsx
// e franqueado.tsx — são telas informativas, sem nada de CRUD.)
// ---------------------------------------------------------------------------

function RedeGerenciar(): JSX.Element {
  const { data: redes, isLoading } = useAdminListarRedesQuery();
  const { data: candidatas, error: erroCandidatas } = useAdminListarLojasCandidatasRedeQuery();
  const [criar] = useAdminCriarRedeMutation();
  const [atualizar] = useAdminAtualizarRedeMutation();
  const [salvarLojas, statusSalvar] = useAdminSalvarLojasDaRedeMutation();

  const [idSelecionada, setIdSelecionada] = useState<number | null>(null);
  const { data: lojasDaRede } = useAdminListarLojasDaRedeQuery(idSelecionada as number, { skip: !idSelecionada });

  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [abertoNova, setAbertoNova] = useState(false);
  const [nomeNova, setNomeNova] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvou, setSalvou] = useState(false);

  // Seleciona a primeira rede automaticamente — com uma só (caso comum), a tela
  // já abre pronta para editar as lojas.
  useEffect(() => {
    if (idSelecionada == null && redes && redes.length > 0) setIdSelecionada(redes[0]!.id);
  }, [redes, idSelecionada]);

  useEffect(() => {
    setMarcadas(new Set((lojasDaRede ?? []).map((l) => l.idConfiguracaoCardapio)));
    setSalvou(false);
  }, [lojasDaRede]);

  /** Lojas da franquia agrupadas por empresa — é assim que o gestor pensa. */
  const porUnidade = useMemo(() => {
    const mapa = new Map<number, { nomeUnidade: string; lojas: NonNullable<typeof candidatas>['lojas'] }>();
    for (const loja of candidatas?.lojas ?? []) {
      const atual = mapa.get(loja.idUnidade) ?? { nomeUnidade: loja.nomeUnidade, lojas: [] };
      atual.lojas.push(loja);
      mapa.set(loja.idUnidade, atual);
    }
    return [...mapa.values()];
  }, [candidatas]);

  const redeSelecionada = redes?.find((r) => r.id === idSelecionada) ?? null;

  function alternar(idLoja: number): void {
    setMarcadas((atual) => {
      const proxima = new Set(atual);
      if (proxima.has(idLoja)) proxima.delete(idLoja);
      else proxima.add(idLoja);
      return proxima;
    });
    setSalvou(false);
  }

  async function criarRede(): Promise<void> {
    setErro(null);
    try {
      const nova = await criar({ nome: nomeNova.trim() }).unwrap();
      setAbertoNova(false);
      setNomeNova('');
      setIdSelecionada(nova.id);
    } catch (err) {
      setErro(mensagemDoErro(err, 'Falha ao criar a rede'));
    }
  }

  async function salvar(): Promise<void> {
    if (idSelecionada == null) return;
    setErro(null);
    try {
      await salvarLojas({ id: idSelecionada, lojas: [...marcadas] }).unwrap();
      setSalvou(true);
    } catch (err) {
      setErro(mensagemDoErro(err, 'Falha ao salvar as lojas da rede'));
    }
  }

  // A franquia é cadastrada no Sischef pelo suporte; sem ela não há lojas
  // candidatas e não adianta deixar o gestor montar rede vazia.
  const semFranquia = erroCandidatas != null;

  return (
    <>
      <HeaderTela
        titulo="Rede de lojas"
        descricao={DESCRICAO_TELA}
        acoes={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAbertoNova(true)}
            disabled={semFranquia}
            fullWidth
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Nova rede
          </Button>
        }
      />

      {semFranquia && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {mensagemDoErro(
            erroCandidatas,
            'Esta empresa ainda não está vinculada a uma franquia no Sischef. Peça ao suporte para cadastrar a franquia e os franqueados.',
          )}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Paper variant="outlined" sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ px: 2, pt: 2 }}>
              Redes
            </Typography>
            <List dense>
              {(redes ?? []).map((r) => (
                <ListItemButton key={r.id} selected={r.id === idSelecionada} onClick={() => setIdSelecionada(r.id)}>
                  <ListItemText primary={r.nome} />
                </ListItemButton>
              ))}
              {(redes ?? []).length === 0 && (
                <ListItem>
                  <ListItemText
                    primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                    primary="Nenhuma rede criada."
                  />
                </ListItem>
              )}
            </List>
          </Paper>

          <Paper variant="outlined" sx={{ width: '100%', p: 2 }}>
            {redeSelecionada == null ? (
              <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                Crie uma rede para escolher as lojas participantes.
              </Typography>
            ) : (
              <>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  justifyContent="space-between"
                >
                  <TextField
                    label="Nome da rede"
                    size="small"
                    defaultValue={redeSelecionada.nome}
                    key={redeSelecionada.id}
                    onBlur={(e) => {
                      const nome = e.target.value.trim();
                      if (nome && nome !== redeSelecionada.nome) {
                        void atualizar({ id: redeSelecionada.id, dados: { nome } });
                      }
                    }}
                    sx={{ minWidth: { sm: 260 } }}
                  />
                  <Chip size="small" label={`${marcadas.size} loja(s) na rede`} />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Lojas da franquia
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Marque as que participam desta rede. Uma loja pode estar em apenas uma rede.
                </Typography>

                {porUnidade.map((grupo) => (
                  <Box key={grupo.nomeUnidade} sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {grupo.nomeUnidade}
                    </Typography>
                    {grupo.lojas.map((loja) => {
                      const emOutraRede = loja.idRedeAtual != null && loja.idRedeAtual !== redeSelecionada.id;
                      return (
                        <Box key={loja.idConfiguracaoCardapio} sx={{ display: 'block' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={marcadas.has(loja.idConfiguracaoCardapio)}
                                disabled={emOutraRede}
                                onChange={() => alternar(loja.idConfiguracaoCardapio)}
                              />
                            }
                            label={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <span>{loja.nome}</span>
                                {emOutraRede && <Chip size="small" label="em outra rede" />}
                              </Stack>
                            }
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ))}

                {porUnidade.length === 0 && !semFranquia && (
                  <Typography color="text.secondary" variant="body2" sx={{ py: 2 }}>
                    Nenhuma loja de cardápio cadastrada nas empresas desta franquia.
                  </Typography>
                )}

                {erro && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {erro}
                  </Alert>
                )}
                {salvou && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Lojas da rede atualizadas.
                  </Alert>
                )}

                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={salvar}
                    disabled={statusSalvar.isLoading}
                    fullWidth
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Salvar lojas
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </Stack>
      )}

      <Dialog open={abertoNova} onClose={() => setAbertoNova(false)} fullWidth maxWidth="xs">
        <DialogTitle>Nova rede</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome da rede"
            value={nomeNova}
            onChange={(e) => setNomeNova(e.target.value)}
            fullWidth
            required
            sx={{ mt: 1 }}
          />
          {erro && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {erro}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAbertoNova(false)}>Cancelar</Button>
          <Button variant="contained" onClick={criarRede} disabled={!nomeNova.trim()}>
            Criar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
